import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { isMobile } from 'react-device-detect'

import { CustomizedClientsResponse } from '@/service/subscriptions'
import { getNavigationIcon } from '@/constants/navigationAssets'
import Logo from '@/components/navigation/Logo'
import vh from '@/utils/100vh'

import { LogoContainer } from './Navbar.styled'

interface NavbarProps {
  short?: boolean
  highlight?: boolean
  clientDetails?: CustomizedClientsResponse
}

interface LogoConfig {
  href: any
  imgSrc?: string
  isNarrow?: boolean
  dataTestId?: string
  LogoComponent?: React.ReactNode
}

interface UseNavbarReturn {
  open: boolean
  scrolled: boolean
  hydrated: boolean
  isLegia: boolean
  isParkingSearch: boolean
  getLogoConfig: () => LogoConfig
  handleToggle: () => void
  handleClose: () => void
  setOpen: (value: boolean) => void
}

export const useNavbar = ({
  short = false,
  highlight = false,
  clientDetails
}: NavbarProps): UseNavbarReturn => {
  const router = useRouter()
  const { t } = useTranslation('common')

  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  const isLegia = router.query.parking === 'legia-warszawa'
  const isParkingSearch =
    router.pathname === '/parking-search' ||
    router.pathname === '/wyszukiwanie-parkingow' ||
    router.pathname === '/parkplatzsuche'
  const currentLogo =
    router?.locale === 'pl'
      ? getNavigationIcon('logoCP')
      : getNavigationIcon('logoDP')

  useEffect(() => {
    const root = document.getElementsByTagName('html')
    root[0].classList.toggle('menu-opened', open)
  }, [open])

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 10)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)
    vh.init()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    setHydrated(true)
  }, [])

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleClose = () => {
    router.push({ pathname: '/' })
  }

  const getLogoConfig = (): LogoConfig => {
    switch (true) {
      case !!clientDetails?.logoUrl:
        return {
          href: clientDetails?.free
            ? { pathname: router.pathname, query: { ...router.query } }
            : { pathname: '/' },
          imgSrc: clientDetails?.logoUrl
        }

      case isLegia:
        return {
          href: { pathname: '/' },
          imgSrc:
            'https://cdn.naviparking.com/digital_parking/img/logo-legia.webp',
          isNarrow: true
        }

      case isParkingSearch && isMobile && !open:
        return {
          href: null,
          dataTestId: 'menu-logo',
          LogoComponent: (
            <LogoContainer>
              <img src={currentLogo} alt="Logo" data-test-id="menu-logo-img" />
              <img
                src={getNavigationIcon('byNaviparking')}
                alt="By Naviparking"
              />
            </LogoContainer>
          )
        }

      default:
        return {
          href: { pathname: '/' },
          dataTestId: 'menu-logo',
          LogoComponent: <Logo open={open} />
        }
    }
  }

  return {
    open,
    scrolled,
    hydrated,
    isLegia,
    isParkingSearch,
    getLogoConfig,
    handleToggle,
    handleClose,
    setOpen
  }
}
